import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { approveQuotationAndCreateWorkOrder, rejectQuotation } from '../../services/quotationFlowService';
import Icon from '../../components/AppIcon';

const QuoteAuthorization = () => {
  const [searchParams] = useSearchParams();
  const quoteId = searchParams?.get('quote_id');

  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [error, setError] = useState('');
  const [action, setAction] = useState(null); // 'approved' | 'rejected'
  const [processing, setProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  useEffect(() => {
    if (quoteId) {
      loadQuote(quoteId);
    } else {
      setError('No se proporcionó un ID de cotización válido.');
      setLoading(false);
    }
  }, [quoteId]);

  const loadQuote = async (id) => {
    try {
      setLoading(true);
      const { data, error: fetchErr } = await supabase
        ?.from('quotations')
        ?.select(`
          *,
          work_order:work_orders(
            id, folio_ot, kilometraje,
            vehicle:vehicles(marca, modelo, year, vin, placas, color, motor),
            client:clients(nombre, telefono, email)
          )
        `)
        ?.eq('id', id)
        ?.maybeSingle();

      if (fetchErr || !data) {
        setError('No se encontró la cotización solicitada.');
        return;
      }

      setQuote(data);
      setWorkOrder(data?.work_order);

      // If already processed, show result
      if (data?.status === 'aprobada') setAction('approved');
      if (data?.status === 'rechazada') setAction('rejected');
    } catch (err) {
      setError('Error al cargar la cotización: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!quote?.id) return;
    setProcessing(true);
    try {
      const items = quote?.items_snapshot || [];
      await approveQuotationAndCreateWorkOrder({
        quotationId: quote?.id,
        workOrderId: quote?.work_order_id,
        quoteItems: items,
        inspectionFindings: [],
      });
      setAction('approved');
    } catch (err) {
      setError('Error al aprobar: ' + err?.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!quote?.id) return;
    setProcessing(true);
    try {
      await rejectQuotation({
        quotationId: quote?.id,
        workOrderId: quote?.work_order_id,
        reason: rejectionReason,
      });
      setAction('rejected');
      setShowRejectForm(false);
    } catch (err) {
      setError('Error al rechazar: ' + err?.message);
    } finally {
      setProcessing(false);
    }
  };

  const vehicle = workOrder?.vehicle;
  const client = workOrder?.client;
  const items = quote?.items_snapshot || [];
  const total = items?.reduce((s, i) => s + ((i?.price || 0) * (i?.quantity || 1)), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Icon name="Loader2" size={48} className="text-primary animate-spin" />
          <p className="text-muted-foreground">Cargando cotización...</p>
        </div>
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-xl p-8 max-w-md w-full text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-heading font-bold text-foreground mb-2">Error</h2>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Icon name="FileCheck" size={40} className="text-primary" />
            <h1 className="text-3xl font-heading font-bold text-foreground">GearControl</h1>
          </div>
          <p className="text-muted-foreground">Autorización de Cotización</p>
        </div>

        {/* Already processed */}
        {action === 'approved' && (
          <div className="bg-success/10 border border-success/30 rounded-xl p-8 text-center">
            <Icon name="CheckCircle2" size={64} className="text-success mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-foreground mb-2">¡Cotización Autorizada!</h2>
            <p className="text-muted-foreground">Su cotización ha sido aprobada. El taller iniciará los trabajos a la brevedad.</p>
            <p className="text-sm text-muted-foreground mt-2">Folio: <strong>{quote?.folio_cotizacion}</strong></p>
          </div>
        )}

        {action === 'rejected' && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-8 text-center">
            <Icon name="XCircle" size={64} className="text-destructive mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-foreground mb-2">Cotización Rechazada</h2>
            <p className="text-muted-foreground">Ha rechazado esta cotización. El taller ha sido notificado.</p>
            <p className="text-sm text-muted-foreground mt-2">Folio: <strong>{quote?.folio_cotizacion}</strong></p>
          </div>
        )}

        {!action && quote && (
          <>
            {/* Quote Info Card */}
            <div className="bg-card border border-border rounded-xl p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="FileText" size={24} className="text-primary" />
                <h2 className="text-xl font-heading font-semibold text-foreground">
                  Cotización {quote?.folio_cotizacion}
                </h2>
              </div>

              {/* Vehicle & Client */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-1">Cliente</p>
                  <p className="font-semibold text-foreground">{client?.nombre || '—'}</p>
                  {client?.telefono && <p className="text-sm text-muted-foreground">{client?.telefono}</p>}
                </div>
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-1">Vehículo</p>
                  <p className="font-semibold text-foreground">
                    {vehicle?.marca} {vehicle?.modelo} {vehicle?.year}
                  </p>
                  {vehicle?.vin && <p className="text-sm text-muted-foreground">VIN: {vehicle?.vin}</p>}
                  {vehicle?.placas && <p className="text-sm text-muted-foreground">Placas: {vehicle?.placas}</p>}
                </div>
              </div>

              {/* Items */}
              {items?.length > 0 ? (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3">Trabajos / Refacciones</h3>
                  <div className="space-y-2">
                    {items?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">{item?.description}</p>
                          {item?.supplier && <p className="text-xs text-muted-foreground">{item?.supplier}</p>}
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-sm text-muted-foreground">x{item?.quantity || 1}</p>
                          <p className="text-sm font-semibold text-foreground">
                            ${((item?.price || 0) * (item?.quantity || 1))?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-border">
                    <span className="font-heading font-bold text-foreground">TOTAL</span>
                    <span className="text-xl font-heading font-bold text-primary">
                      ${total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">El taller le enviará el detalle de partidas por separado.</p>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Reject form */}
            {showRejectForm && (
              <div className="bg-card border border-border rounded-xl p-6 mb-4">
                <h3 className="font-semibold text-foreground mb-3">Motivo de rechazo (opcional)</h3>
                <textarea
                  className="w-full border border-border rounded-lg p-3 text-sm bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={3}
                  placeholder="Indique el motivo por el que rechaza esta cotización..."
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e?.target?.value)}
                />
                <div className="flex gap-3 mt-3">
                  <button
                    onClick={handleReject}
                    disabled={processing}
                    className="flex-1 bg-destructive text-white py-3 rounded-lg font-semibold hover:bg-destructive/90 disabled:opacity-50 transition-colors"
                  >
                    {processing ? 'Procesando...' : 'Confirmar Rechazo'}
                  </button>
                  <button
                    onClick={() => setShowRejectForm(false)}
                    disabled={processing}
                    className="flex-1 border border-border text-foreground py-3 rounded-lg font-semibold hover:bg-muted transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {!showRejectForm && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="flex items-center justify-center gap-3 bg-success text-white py-4 rounded-xl font-heading font-bold text-lg hover:bg-success/90 disabled:opacity-50 transition-colors"
                >
                  <Icon name="CheckCircle2" size={24} />
                  {processing ? 'Procesando...' : 'AUTORIZAR'}
                </button>
                <button
                  onClick={() => setShowRejectForm(true)}
                  disabled={processing}
                  className="flex items-center justify-center gap-3 border-2 border-destructive text-destructive py-4 rounded-xl font-heading font-bold text-lg hover:bg-destructive/10 disabled:opacity-50 transition-colors"
                >
                  <Icon name="XCircle" size={24} />
                  RECHAZAR
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QuoteAuthorization;
